import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, CreditCard, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DyvysCheckoutPage() {
  const { itemType, itemId } = useParams(); // itemType: "episode" ou "series"
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [phone, setPhone] = useState("");
  const [provider, setProvider] = useState("orange_money");

  useEffect(() => {
    if (!user) {
      toast.error("Veuillez vous connecter");
      navigate("/login");
      return;
    }
    fetchItemDetails();
  }, [itemId, itemType, user]);

  const fetchItemDetails = async () => {
    try {
      if (itemType === "episode") {
        // Récupérer les détails de l'épisode
        const seriesResponse = await fetch(`${API_URL}/api/dyvys/series`);
        const seriesData = await seriesResponse.json();
        
        for (const s of seriesData.series) {
          const detailResponse = await fetch(`${API_URL}/api/dyvys/series/${s.series_id}`);
          const detailData = await detailResponse.json();
          
          const ep = detailData.episodes.find(e => e.episode_id === itemId);
          if (ep) {
            setItem({ ...ep, type: "episode", seriesTitle: detailData.series.title });
            break;
          }
        }
      } else if (itemType === "series") {
        // Récupérer les détails de la série
        const response = await fetch(`${API_URL}/api/dyvys/series/${itemId}`);
        const data = await response.json();
        
        const totalPrice = data.episodes.reduce((sum, ep) => sum + (ep.price_cdf || 0), 0);
        setItem({
          ...data.series,
          type: "series",
          totalEpisodes: data.episodes.length,
          price_cdf: totalPrice,
          price_usd: totalPrice / 2000 // Approximation
        });
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Impossible de charger les détails");
      navigate("/dyvys");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    
    if (!phone) {
      toast.error("Veuillez entrer votre numéro de téléphone");
      return;
    }

    setProcessing(true);

    try {
      // Étape 1: Créer le paiement
      const purchaseResponse = await fetch(`${API_URL}/api/dyvys/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: itemType,
          item_id: itemId,
          provider: provider,
          customer_phone: phone,
          customer_name: user.name || user.phone
        })
      });

      if (!purchaseResponse.ok) {
        throw new Error("Erreur lors du paiement");
      }

      const purchaseData = await purchaseResponse.json();

      // Étape 2: Accorder l'accès (simulation - en production, cela se fait via webhook)
      const grantResponse = await fetch(`${API_URL}/api/dyvys/grant-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_id: purchaseData.payment_id,
          user_id: user.user_id,
          item_type: itemType,
          item_id: itemId
        })
      });

      if (!grantResponse.ok) {
        throw new Error("Erreur lors de l'octroi d'accès");
      }

      toast.success("Paiement réussi! Vous avez maintenant accès.");
      
      // Rediriger vers la série ou l'épisode
      if (itemType === "episode") {
        navigate(`/dyvys/watch/${itemId}`);
      } else {
        navigate(`/dyvys/series/${itemId}`);
      }

    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.message || "Erreur lors du paiement");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-purple-800/50 backdrop-blur-sm bg-black/30">
        <div className="container mx-auto px-4 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-purple-300 hover:text-purple-100 hover:bg-purple-500/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </header>

      {/* Checkout Form */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-4xl font-bold mb-8 text-center">
            Finaliser votre achat
          </h1>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Order Summary */}
            <Card className="bg-gradient-to-br from-gray-800/50 to-purple-900/30 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {item.type === "episode" ? (
                  <>
                    <div>
                      <p className="text-gray-400 text-sm">Série</p>
                      <p className="text-white font-semibold">{item.seriesTitle}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Épisode</p>
                      <p className="text-white font-semibold">
                        Épisode {item.episode_number}: {item.title}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-gray-400 text-sm">Série complète</p>
                      <p className="text-white font-semibold text-xl">{item.title}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Nombre d'épisodes</p>
                      <p className="text-white font-semibold">{item.totalEpisodes} épisodes</p>
                    </div>
                  </>
                )}
                
                <div className="border-t border-purple-500/30 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Prix</span>
                    <span className="text-2xl font-bold text-green-400">
                      {item.price_cdf} CDF
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm text-right mt-1">
                    ≈ ${item.price_usd?.toFixed(2) || (item.price_cdf / 2000).toFixed(2)} USD
                  </p>
                </div>

                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-300 font-semibold text-sm">Accès immédiat</p>
                      <p className="text-green-400/80 text-xs">
                        Regardez directement après le paiement
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Form */}
            <Card className="bg-gradient-to-br from-gray-800/50 to-purple-900/30 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  Paiement Mobile Money
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Payer via votre opérateur mobile
                </CardDescription>
              </CardHeader>
              <form onSubmit={handlePurchase}>
                <CardContent className="space-y-6">
                  {/* Phone Number */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white">
                      Numéro de téléphone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+243 999 999 999"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="bg-gray-900/50 border-purple-500/30 text-white"
                      required
                    />
                    <p className="text-xs text-gray-500">
                      Le numéro qui recevra la demande de paiement
                    </p>
                  </div>

                  {/* Provider Selection */}
                  <div className="space-y-3">
                    <Label className="text-white">Opérateur Mobile Money</Label>
                    <RadioGroup value={provider} onValueChange={setProvider}>
                      <div className="flex items-center space-x-2 bg-gray-900/50 p-3 rounded-lg border border-purple-500/30">
                        <RadioGroupItem value="orange_money" id="orange" />
                        <Label htmlFor="orange" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Smartphone className="w-5 h-5 text-orange-500" />
                          <span className="text-white">Orange Money</span>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 bg-gray-900/50 p-3 rounded-lg border border-purple-500/30">
                        <RadioGroupItem value="mpesa_drc" id="mpesa" />
                        <Label htmlFor="mpesa" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Smartphone className="w-5 h-5 text-green-500" />
                          <span className="text-white">M-Pesa (Vodacom)</span>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 bg-gray-900/50 p-3 rounded-lg border border-purple-500/30">
                        <RadioGroupItem value="airtel_money" id="airtel" />
                        <Label htmlFor="airtel" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Smartphone className="w-5 h-5 text-red-500" />
                          <span className="text-white">Airtel Money</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={processing}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg"
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                        Traitement...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Payer {item.price_cdf} CDF
                      </>
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
